import { useEffect } from "react";

/**
 * Touch DnD bridge (M3.4).
 *
 * The Anvil DnD pipeline is driven by mouse events on per-canvas
 * AnvilDnDListener overlays (see useAnvilDnDEvents), plus document/window
 * level mouse listeners (drop commit, drag deactivation, drag preview,
 * drag-to-scroll). Touch interactions never produce those events:
 * - pointer events from a touch are implicitly captured to the element the
 *   gesture started on (the dragged widget), so overlays never hear them;
 * - compatibility mouse events are not fired during a touch drag.
 *
 * This hook translates touch/pen pointer events into the SAME synthetic
 * MouseEvent contract the pipeline already consumes — the established
 * pattern used for detached (modal) widgets, which re-dispatch
 * `new MouseEvent("mousemove", ...)` onto listener elements. Because the
 * synthetic events are real, bubbling DOM mouse events, every existing mouse
 * consumer (highlight tracking, drop commit, deactivation, drag preview,
 * drag-to-scroll) works for touch without modification, and the mouse path
 * itself is untouched.
 *
 * Targeting uses document.elementFromPoint — the same hit-testing the
 * browser performs for real mouse events — so the topmost DnD listener
 * overlay under the finger receives the events, exactly as it would under
 * the cursor.
 */

const isTouchLikePointer = (e: PointerEvent) => e.pointerType !== "mouse";

const buildSyntheticMouseEvent = (type: string, e: PointerEvent) =>
  new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    clientX: e.clientX,
    clientY: e.clientY,
    view: window,
  });

export const useAnvilTouchDnDBridge = (isDragging: boolean) => {
  useEffect(
    function bridgeTouchPointerEventsToMouseDnD() {
      if (!isDragging) return;

      let lastHitTarget: Element | null = null;

      const hitTest = (e: PointerEvent) =>
        document.elementFromPoint(e.clientX, e.clientY);

      const onPointerMove = (e: PointerEvent) => {
        if (!isTouchLikePointer(e)) return;

        const target = hitTest(e);

        if (lastHitTarget && lastHitTarget !== target) {
          // Leaving an element: real mice fire mouseout; canvases rely on it
          // to deactivate (setDraggingCanvas("")).
          lastHitTarget.dispatchEvent(buildSyntheticMouseEvent("mouseout", e));
        }

        lastHitTarget = target;
        (target ?? document.body).dispatchEvent(
          buildSyntheticMouseEvent("mousemove", e),
        );
      };

      const onPointerUp = (e: PointerEvent) => {
        if (!isTouchLikePointer(e)) return;

        const target = hitTest(e) ?? document.body;

        lastHitTarget = null;
        // Bubbles through the document: commits the drop via the capture-phase
        // document mouseup listener (useAnvilDnDEvents) and ends the drag via
        // the body/window mouseup listeners (useAnvilDnDDeactivation).
        target.dispatchEvent(buildSyntheticMouseEvent("mouseup", e));
      };

      const onPointerCancel = (e: PointerEvent) => {
        if (!isTouchLikePointer(e)) return;

        if (lastHitTarget) {
          lastHitTarget.dispatchEvent(buildSyntheticMouseEvent("mouseout", e));
        }

        lastHitTarget = null;
        // Dispatch directly on window: window is the entire propagation path,
        // so the drag ends (useAnvilDnDDeactivation listens on window) WITHOUT
        // reaching the document/canvas mouseup listeners — no drop commit on a
        // cancelled gesture.
        window.dispatchEvent(buildSyntheticMouseEvent("mouseup", e));
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
      document.addEventListener("pointercancel", onPointerCancel);

      return () => {
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        document.removeEventListener("pointercancel", onPointerCancel);
      };
    },
    [isDragging],
  );
};
